
import { useRef, useEffect, useState } from 'react';

const platforms = [
  {
    id: 'dailyway',
    name: 'Dailyway',
    subtitle: 'AI 微型工具整合平台',
    url: 'https://dailyway.ai?utm_source=enchengwebsite&utm_medium=aiplans&utm_campaign=aiplans202603&utm_id=aiplans',
    displayUrl: 'dailyway.ai',
    description: '將日常工作中最常用的 AI 功能整合於單一平台，提供輕量、快速、易用的微型工具集，讓個人與企業團隊無需技術背景，即可輕鬆駕馭 AI 的力量，大幅提升每日工作效率。',
    features: ['智慧文案生成', '資料摘要分析', '多工具一站整合', '零門檻快速上手'],
    icon: 'ri-tools-line',
    gradient: 'from-teal-500 to-emerald-500',
    lightBg: 'from-teal-50 to-emerald-50',
    accent: 'teal',
    image: 'https://static.readdy.ai/image/21854ded7812c3e1f6e0237d0c8099eb/26033925265ade41903b4fd38e2fa0e2.jpeg',
  },
  {
    id: 'cfoguard',
    name: '雲守護財務長',
    subtitle: '智慧財務分析平台',
    url: 'mailto:enchengai.com@gmail.com',
    displayUrl: 'cfoguard.zeabur.app',
    description: '專為中小企業打造的 AI 財務智慧平台，即時監控財務健康狀況、自動生成財務報告、預測現金流風險，讓企業主擁有媲美大型企業財務長的決策支援能力，守護每一分資金安全。',
    features: ['即時財務儀表板', '現金流預測分析', '自動財報生成', 'AI 風險預警'],
    icon: 'ri-shield-check-line',
    gradient: 'from-emerald-500 to-teal-600',
    lightBg: 'from-emerald-50 to-teal-50',
    accent: 'emerald',
    image: 'https://public.readdy.ai/ai/img_res/0b440f5589edd5663b46036c539b2a26.jpg',
  },
];

function PlatformCard({ platform, index }: { platform: typeof platforms[0]; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`flex flex-col lg:flex-row items-center gap-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image */}
      <div className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
          <div className="w-full h-72 lg:h-80">
            <img
              src={platform.image}
              alt={platform.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
          </div>
          <div className={`absolute inset-0 bg-gradient-to-t ${platform.lightBg} opacity-20`}></div>
          {/* URL badge */}
          <div className="absolute bottom-4 left-4">
            <a
              href={platform.url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-md rounded-full text-xs font-medium text-gray-700 shadow-lg hover:bg-white transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-teal-500"></i>
              {platform.displayUrl}
            </a>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`w-full lg:w-1/2 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
        <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${platform.lightBg} rounded-full mb-5 border border-teal-100`}>
          <div className={`w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br ${platform.gradient}`}>
            <i className={`${platform.icon} text-white text-xs`}></i>
          </div>
          <span className="text-sm font-medium text-teal-700">{platform.subtitle}</span>
        </div>

        <h3 className="text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
          {platform.name}
        </h3>

        <p className="text-gray-500 text-sm leading-relaxed mb-7">
          {platform.description}
        </p>

        <ul className="grid grid-cols-2 gap-3 mb-8">
          {platform.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
              <div className={`w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br ${platform.gradient} flex-shrink-0 shadow-sm`}>
                <i className="ri-check-line text-white text-xs"></i>
              </div>
              {f}
            </li>
          ))}
        </ul>

        <a
          href={platform.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${platform.gradient} text-white rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300`}
        >
          前往平台
          <i className="ri-arrow-right-up-line"></i>
        </a>
      </div>
    </div>
  );
}

export default function PlatformsSection() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="platforms" className="py-24 bg-gradient-to-b from-white to-teal-50/40">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center mb-20 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-600 mb-5 border border-teal-100">
            <i className="ri-layout-grid-line"></i>
            <span className="text-sm font-medium">自營平台</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            我們打造的數位平台
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            恩誠國際不只提供顧問服務，更親身實踐 AI 應用，自主研發兩大平台，為企業與個人帶來真實可用的數位工具。
          </p>
        </div>

        {/* Platform Cards */}
        <div className="flex flex-col gap-20">
          {platforms.map((platform, index) => (
            <PlatformCard key={platform.id} platform={platform} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
